import { Api } from '../api';
import {
  GetAllGroupsResponse,
  GetAllGroupsRequest,
  CreateGroupsResponse,
  CreateGroupsRequest,
  UpdateGroupsResponse,
  UpdateGroupsRequest,
  DeleteGroupsRequest,
  CreateFolderResponse,
  CreateFolderRequest,
  RenameFolderRequest,
  RenameFolderResponse,
  DeleteFolderRequest,
  DeleteFolderResponse,
  MoveFolderResponse,
  MoveFolderRequest,
  GetGroupMembersResponse,
  GetGroupMembersRequest,
  DeleteGroupMembersResponse,
  DeleteGroupMembersRequest,
  AddGroupMembersResponse,
  AddGroupMembersRequest,
  MoveGroupsResponse,
  MoveGroupsRequest,
  GroupMembersFilterResponse,
  GetGroupByIdResponse,
  GetGroupByIdRequest,
  GetGroupTransitionsResponse,
  GetGroupTransitionsRequest,
  UpdateGroupTransitionsResponse,
  UpdateGroupTransitionsRequest,
  MoveGroupMembersResponse,
  MoveGroupMembersRequest,
  GetAlgorithmTypesRequest,
  GetCustomerGroupTransitionsRequest,
  GetCustomerGroupTransitionsResponse,
} from './model';
import { mapDataToInputItems } from '../filter/helpers';
import { addListInputItems } from '../filter/slice';
import { ListInputItemOptions } from '../filter/model';

export const groupsApi = Api.injectEndpoints({
  endpoints: (builder) => ({
    getAllGroups: builder.query<GetAllGroupsResponse[], GetAllGroupsRequest>({
      query(arg) {
        const { customerGroupTypes, ...otherParams } = arg;
        const params = customerGroupTypes
          ?.map((item) => `customerGroupTypes[]=${item}`)
          ?.join('&');
        return {
          url: `/hub/orgs/api/customer-group?${params}`,
          method: 'GET',
          params: {
            ...otherParams,
            random: Math.random(),
          },
        };
      },
    }),
    //
    getGroupById: builder.query<GetGroupByIdResponse, GetGroupByIdRequest>({
      query(arg) {
        const { groupId, ...otherParams } = arg;
        return {
          url: `hub/orgs/api/customer-group/${groupId}`,
          method: 'GET',
          params: otherParams,
        };
      },
    }),

    createGroup: builder.mutation<CreateGroupsResponse[], CreateGroupsRequest>({
      query(arg) {
        const { body, ...otherParams } = arg;
        return {
          url: `/hub/orgs/api/customer-group`,
          method: 'POST',
          params: otherParams,
          body: body,
        };
      },
    }),

    updateGroup: builder.mutation<UpdateGroupsResponse, UpdateGroupsRequest>({
      query(arg) {
        const { groupId, body, ...otherParams } = arg;
        return {
          url: `/hub/orgs/api/customer-group/${groupId}`,
          method: 'PUT',
          params: otherParams,
          body: body,
        };
      },
    }),

    moveGroup: builder.mutation<MoveGroupsResponse, MoveGroupsRequest>({
      query(arg) {
        const { groupId, body, ...params } = arg;
        return {
          url: `/hub//orgs/api/customer-group/${groupId}/move`,
          method: 'PUT',
          params: params,
          body: body,
        };
      },
    }),

    deleteGroup: builder.mutation<any, DeleteGroupsRequest>({
      query(arg) {
        const { groupId, ...params } = arg;
        return {
          url: `/hub/orgs/api/customer-group/${groupId}`,
          method: 'DELETE',
          params: params,
        };
      },
    }),

    createGroupFolder: builder.mutation<
      CreateFolderResponse,
      CreateFolderRequest
    >({
      query(arg) {
        const { body, ...otherParams } = arg;
        return {
          url: `/hub/orgs/api/folders`,
          method: 'POST',
          params: otherParams,
          body,
        };
      },
    }),

    renameGroupFolder: builder.mutation<
      RenameFolderResponse,
      RenameFolderRequest
    >({
      query(arg) {
        const { body, sellerWorkspaceId, folderId } = arg;
        return {
          url: `/hub/orgs/api/folders/${folderId}/rename`,
          method: 'PUT',
          params: { sellerWorkspaceId },
          body,
        };
      },
    }),

    deleteGroupFolder: builder.mutation<
      DeleteFolderResponse,
      DeleteFolderRequest
    >({
      query(arg) {
        const { sellerWorkspaceId, folderId } = arg;
        return {
          url: `/hub/orgs/api/folders/${folderId}`,
          method: 'DELETE',
          params: { sellerWorkspaceId },
        };
      },
    }),

    moveGroupFolder: builder.mutation<MoveFolderResponse, MoveFolderRequest>({
      query(arg) {
        const { sellerWorkspaceId, folderId, body } = arg;
        return {
          url: `/hub/orgs/api/folders/${folderId}/move`,
          method: 'PUT',
          params: { sellerWorkspaceId },
          body,
        };
      },
    }),

    getGroupTransitions: builder.query<
      GetGroupTransitionsResponse[],
      GetGroupTransitionsRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/customer-group/life-cycle-transitions`,
          method: 'GET',
          params: arg,
        };
      },
    }),

    updateGroupTransitions: builder.mutation<
      UpdateGroupTransitionsResponse[],
      UpdateGroupTransitionsRequest
    >({
      query(arg) {
        const { transitionId, body, ...otherParams } = arg;
        return {
          url: `/hub/orgs/api/customer-group/life-cycle-transitions/${transitionId}`,
          method: 'POST',
          params: otherParams,
          body,
        };
      },
    }),

    getGroupMembers: builder.query<
      GetGroupMembersResponse,
      GetGroupMembersRequest
    >({
      query(arg) {
        const { groupId, cfaIds = [], divisionIds = [], ...rest } = arg;

        const searchParams = new URLSearchParams();

        cfaIds.forEach((id) => searchParams.append('cfaIds', id));
        divisionIds.forEach((id) => searchParams.append('divisionIds', id));
        Object.entries(rest).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });
        searchParams.append('isReturnNewSemanticConversations', 'true');
        return {
          url: `/hub/orgs/api/customer-group/${groupId}/members?${searchParams.toString()}`,
          method: 'GET',
          //   params: params,
          cache: 'no-cache',
        };
      },
      onQueryStarted(request, { dispatch, queryFulfilled }) {
        queryFulfilled.then((res) => {
          dispatch(
            groupsApi.endpoints.getGroupCustomerListFilters.initiate({
              groupId: request?.groupId,
              sellerWorkspaceId: request?.sellerWorkspaceId,
            })
          );
        });
      },
    }),

    addGroupMember: builder.mutation<
      AddGroupMembersResponse,
      AddGroupMembersRequest
    >({
      query(arg) {
        const { groupId, body, ...params } = arg;
        return {
          url: `/hub/orgs/api/customer-group/${groupId}/members`,
          method: 'POST',
          params: params,
          body,
        };
      },
    }),

    moveGroupMembers: builder.mutation<
      MoveGroupMembersResponse,
      MoveGroupMembersRequest
    >({
      query(arg) {
        const { body, ...otherParams } = arg;
        return {
          url: `/hub/orgs/api/customer-group/move-customers`,
          method: 'POST',
          params: otherParams,
          body,
        };
      },
    }),

    deleteGroupMembers: builder.mutation<
      DeleteGroupMembersResponse,
      DeleteGroupMembersRequest
    >({
      query(arg) {
        const { groupId, body, ...params } = arg;
        return {
          url: `/hub/orgs/api/customer-group/${groupId}/members`,
          method: 'DELETE',
          params: params,
          body,
        };
      },
    }),

    getGroupCustomerListFilters: builder.query<GroupMembersFilterResponse, any>(
      {
        query(arg) {
          return {
            url: `/hub/orgs/api/customer-group/filters/${arg?.groupId}`,
            method: 'GET',
            params: {
              sellerWorkspaceId: arg?.sellerWorkspaceId,
            },
          };
        },
        onQueryStarted(request, { dispatch, queryFulfilled }) {
          queryFulfilled.then((res) => {
            const cfaInputItems = mapDataToInputItems(
              res?.data?.cfas,
              'id',
              'name'
            );
            const divisionInputItems = mapDataToInputItems(
              res?.data?.divisions,
              'id',
              'name'
            )?.filter((i: any) => i?.id && i?.value);
            // dispatch(
            //   updateSiftByScreen({
            //     screen: SiftScreenEnum.GROUPS_SCREEN,
            //     filters: {
            //       cfa: cfaInputItems,
            //       division: divisionInputItems,
            //     },
            //   })
            // );
            dispatch(
              addListInputItems({
                inputItems: divisionInputItems,
                key: ListInputItemOptions.divisions,
              })
            );
            dispatch(
              addListInputItems({
                inputItems: cfaInputItems,
                key: ListInputItemOptions.cfa,
              })
            );
          });
        },
      }
    ),

    getAlgorithmTypes: builder.query<string[], GetAlgorithmTypesRequest>({
      query(arg) {
        return {
          url: `/hub/orgs/api/customer-group/algorithm-types`,
          method: 'GET',
          params: arg,
        };
      },
    }),

    getCustomerGroupTransitions: builder.query<
      GetCustomerGroupTransitionsResponse,
      GetCustomerGroupTransitionsRequest
    >({
      query(arg) {
        return {
          url: `/hub/orgs/api/customer-group/customer-group-transitions`,
          method: 'GET',
          params: arg,
        };
      },
    }),
  }),
});

export const {
  useLazyGetAllGroupsQuery,
  useCreateGroupMutation,
  useUpdateGroupMutation,
  useMoveGroupMutation,
  useDeleteGroupMutation,
  useLazyGetGroupMembersQuery,
  useAddGroupMemberMutation,
  useDeleteGroupMembersMutation,
  useMoveGroupFolderMutation,
  useCreateGroupFolderMutation,
  useDeleteGroupFolderMutation,
  useRenameGroupFolderMutation,
  useLazyGetGroupByIdQuery,
  useLazyGetGroupTransitionsQuery,
  useUpdateGroupTransitionsMutation,
  useMoveGroupMembersMutation,
  useLazyGetAlgorithmTypesQuery,
  useLazyGetCustomerGroupTransitionsQuery,
} = groupsApi;
